/**
 * Négociation de formats et tailles d'images.
 *
 * Chaque visuel de `public/img` est décliné :
 *  - en AVIF (plus léger) à côté de sa version WebP ;
 *  - en plusieurs largeurs (`-400w`, `-600w`, …) pour alimenter `srcset`.
 *
 * Les navigateurs choisissent le meilleur couple format/taille, ce qui réduit
 * fortement le poids du LCP sur iPhone (DPR 2–3, viewport ~390px).
 */

type ImageMeta = { w: number; h: number; widths: number[] };

/** Dimensions intrinsèques + largeurs disponibles pour chaque visuel de /img. */
export const IMAGE_MANIFEST: Record<string, ImageMeta> = {
  "beachwear-sunset": { w: 800, h: 533, widths: [400, 600, 800] },
  "bijoux-accessoires": { w: 800, h: 1067, widths: [400, 600, 800] },
  "editorial-1": { w: 900, h: 1126, widths: [400, 600, 800, 900] },
  "editorial-2": { w: 900, h: 1126, widths: [400, 600, 800, 900] },
  "editorial-3": { w: 900, h: 1126, widths: [400, 600, 800, 900] },
  "editorial-4": { w: 900, h: 1126, widths: [400, 600, 800, 900] },
  "editorial-5": { w: 900, h: 1126, widths: [400, 600, 800, 900] },
  "editorial-banner-desktop": { w: 1600, h: 901, widths: [400, 600, 800, 1200, 1600] },
  "editorial-banner-mobile": { w: 900, h: 1598, widths: [400, 600, 800, 900] },
  "ensemble-sunset-orange-1": { w: 928, h: 1152, widths: [400, 600, 800, 928] },
  "ensemble-sunset-orange-2": { w: 928, h: 1152, widths: [400, 600, 800, 928] },
  "ensemble-sunset-orange-3": { w: 928, h: 1152, widths: [400, 600, 800, 928] },
  "ensemble-sunset-rose-1": { w: 928, h: 1152, widths: [400, 600, 800, 928] },
  "ensemble-sunset-rose-2": { w: 928, h: 1152, widths: [400, 600, 800, 928] },
  "ensemble-sunset-rose-3": { w: 928, h: 1152, widths: [400, 600, 800, 928] },
  "ensemble-capri-blanc-1": { w: 928, h: 1152, widths: [400, 600, 800, 928] },
  "ensemble-capri-blanc-2": { w: 928, h: 1152, widths: [400, 600, 800, 928] },
  "ensemble-capri-blanc-3": { w: 928, h: 1152, widths: [400, 600, 800, 928] },
  "cabas-flora-1": { w: 928, h: 911, widths: [400, 600, 800, 928] },
  "cabas-flora-2": { w: 928, h: 927, widths: [400, 600, 800, 928] },
  "cabas-flora-3": { w: 928, h: 645, widths: [400, 600, 800, 928] },
  "cabas-flora-4": { w: 928, h: 586, widths: [400, 600, 800, 928] },
  "cabas-riviera-1": { w: 928, h: 1152, widths: [400, 600, 800, 928] },
  "cabas-riviera-2": { w: 928, h: 1152, widths: [400, 600, 800, 928] },
  "cabas-riviera-3": { w: 928, h: 1152, widths: [400, 600, 800, 928] },
  "cabas-riviera-4": { w: 928, h: 1152, widths: [400, 600, 800, 928] },
  "cabas-riviera-5": { w: 928, h: 1152, widths: [400, 600, 800, 928] },
  "cabas-naturel-1": { w: 640, h: 602, widths: [400, 600, 640] },
  "cabas-naturel-2": { w: 928, h: 928, widths: [400, 600, 800, 928] },
  "cabas-naturel-3": { w: 928, h: 928, widths: [400, 600, 800, 928] },
  "cabas-naturel-4": { w: 640, h: 600, widths: [400, 600, 640] },
  "kimono-emeraude-1": { w: 928, h: 1299, widths: [400, 600, 800, 928] },
  "kimono-emeraude-2": { w: 928, h: 1299, widths: [400, 600, 800, 928] },
  "kimono-emeraude-3": { w: 928, h: 1299, widths: [400, 600, 800, 928] },
  "kimono-emeraude-4": { w: 500, h: 700, widths: [400, 500] },
  "kimono-paon-boheme-1": { w: 1080, h: 1620, widths: [400, 600, 800, 1080] },
  "kimono-paon-boheme-2": { w: 1080, h: 1620, widths: [400, 600, 800, 1080] },
  "kimono-paon-boheme-3": { w: 1080, h: 1620, widths: [400, 600, 800, 1080] },
  "kimono-paon-boheme-4": { w: 1080, h: 1620, widths: [400, 600, 800, 1080] },
  "kimono-paisley-noir": { w: 1080, h: 1440, widths: [400, 600, 800, 1080] },
  "kimono-paisley-rose": { w: 1080, h: 1440, widths: [400, 600, 800, 1080] },
  "kimono-paisley-blanc": { w: 1080, h: 1440, widths: [400, 600, 800, 1080] },
  "kimono-paisley-bleu": { w: 1080, h: 1440, widths: [400, 600, 800, 1080] },
  "kimono-paisley-couleurs": { w: 1080, h: 1440, widths: [400, 600, 800, 1080] },

  "maillot-riviera-floral-1": { w: 1024, h: 1536, widths: [400, 600, 800, 1024] },
  "maillot-riviera-floral-2": { w: 1080, h: 1350, widths: [400, 600, 800, 1080] },
  "maillot-riviera-floral-3": { w: 800, h: 800, widths: [400, 600, 800] },
  "maillot-riviera-floral-4": { w: 800, h: 800, widths: [400, 600, 800] },
  "maillot-riviera-floral-5": { w: 800, h: 800, widths: [400, 600, 800] },

  "maillot-hazel-1": { w: 1080, h: 1156, widths: [400, 600, 800, 1080] },
  "maillot-hazel-2": { w: 720, h: 720, widths: [400, 600, 720] },
  "maillot-hazel-3": { w: 750, h: 750, widths: [400, 600, 750] },
  "maillot-hazel-4": { w: 750, h: 750, widths: [400, 600, 750] },
  "maillot-hazel-5": { w: 800, h: 800, widths: [400, 600, 800] },
  "maillot-hazel-6": { w: 1080, h: 1118, widths: [400, 600, 800, 1080] },

  "maillot-tropic-1": { w: 1080, h: 1441, widths: [400, 600, 800, 1080] },
  "maillot-tropic-2": { w: 1080, h: 1440, widths: [400, 600, 800, 1080] },
  "maillot-tropic-3": { w: 1080, h: 1080, widths: [400, 600, 800, 1080] },



  "cabas-naturel-5": { w: 910, h: 608, widths: [400, 600, 800, 910] },
  foulards: { w: 800, h: 1067, widths: [400, 600, 800] },
  "insta-1": { w: 800, h: 800, widths: [400, 600, 800] },
  "insta-2": { w: 800, h: 800, widths: [400, 600, 800] },
  "insta-3": { w: 800, h: 800, widths: [400, 600, 800] },
  "insta-4": { w: 800, h: 800, widths: [400, 600, 800] },
  "insta-5": { w: 800, h: 800, widths: [400, 600, 800] },
  "insta-6": { w: 800, h: 800, widths: [400, 600, 800] },
  "hero-mobile-sunset": { w: 900, h: 1200, widths: [400, 600, 800, 900] },
  "hero-sunset": { w: 1600, h: 740, widths: [400, 600, 800, 1200, 1600] },
  "kimonos-coverups": { w: 800, h: 533, widths: [400, 600, 800] },
  "maillots-de-bain": { w: 800, h: 1421, widths: [400, 600, 800] },
  sacs: { w: 800, h: 1067, widths: [400, 600, 800] },
  "bracelets-boheme-1": { w: 1080, h: 1434, widths: [400, 600, 800, 1080] },
  "bracelets-boheme-2": { w: 1080, h: 1057, widths: [400, 600, 800, 1080] },
  "bracelets-boheme-3": { w: 1080, h: 1434, widths: [400, 600, 800, 1080] },
  "bracelets-boheme-4": { w: 1080, h: 1434, widths: [400, 600, 800, 1080] },
  "foulard-paisley-bleu-marine": { w: 1023, h: 1537, widths: [400, 600, 800, 1023] },
  "foulard-paisley-bleu-ciel": { w: 1023, h: 1537, widths: [400, 600, 800, 1023] },
  "foulard-paisley-vert": { w: 1023, h: 1537, widths: [400, 600, 800, 1023] },
  "foulard-paisley-beige": { w: 1023, h: 1537, widths: [400, 600, 800, 1023] },
  "foulard-paisley-gris": { w: 1023, h: 1537, widths: [400, 600, 800, 1023] },
  "foulard-paisley-couleurs": { w: 1000, h: 1000, widths: [400, 600, 800, 1000] },
  "foulard-casquette-noir": { w: 1023, h: 1537, widths: [400, 600, 800, 1023] },
  "foulard-casquette-rouge": { w: 1024, h: 1535, widths: [400, 600, 800, 1024] },
  "foulard-casquette-blanc": { w: 1024, h: 1536, widths: [400, 600, 800, 1024] },
  "foulard-casquette-blanc-2": { w: 1024, h: 1536, widths: [400, 600, 800, 1024] },
  "foulard-casquette-bleu": { w: 1024, h: 1536, widths: [400, 600, 800, 1024] },
  "combishort-crochet-raye-1": { w: 928, h: 1237, widths: [400, 600, 928] },
  "combishort-crochet-raye-2": { w: 928, h: 1237, widths: [400, 600, 928] },
  "combishort-crochet-raye-3": { w: 928, h: 1237, widths: [400, 600, 928] },
  "combishort-crochet-raye-4": { w: 928, h: 1218, widths: [400, 600, 928] },
  "jupe-crochet-blanche-1": { w: 928, h: 1237, widths: [400, 600, 928] },
  "jupe-crochet-blanche-2": { w: 750, h: 1000, widths: [400, 600, 750] },
  "jupe-crochet-blanche-3": { w: 750, h: 1000, widths: [400, 600, 750] },
  "jupe-crochet-blanche-4": { w: 750, h: 1000, widths: [400, 600, 750] },
  "robe-riviera-brown-1": { w: 640, h: 640, widths: [400, 600, 640] },
  "robe-riviera-brown-2": { w: 640, h: 640, widths: [400, 600, 640] },
  "robe-riviera-pink-1": { w: 640, h: 640, widths: [400, 600, 640] },
  "robe-riviera-pink-2": { w: 640, h: 640, widths: [400, 600, 640] },
  "robe-riviera-white-1": { w: 640, h: 640, widths: [400, 600, 640] },
  "robe-riviera-white-2": { w: 640, h: 640, widths: [400, 600, 640] },
  "robe-riviera-black-1": { w: 640, h: 640, widths: [400, 600, 640] },
  "robe-riviera-black-2": { w: 640, h: 640, widths: [400, 600, 640] },
  "robe-riviera-yellow-1": { w: 640, h: 640, widths: [400, 600, 640] },
  "robe-riviera-yellow-2": { w: 640, h: 640, widths: [400, 600, 640] },
  "shop-the-look": { w: 900, h: 1201, widths: [400, 600, 800, 900] },
};

export const AVIF_TYPE = "image/avif";
export const WEBP_TYPE = "image/webp";

/** Extrait la clé du manifeste depuis une URL `/img/xxx.webp|avif`. */
function keyFor(src?: string): string | undefined {
  if (!src || !src.startsWith("/img/")) return undefined;
  const name = src.slice("/img/".length).replace(/\.(webp|avif)$/, "");
  return name in IMAGE_MANIFEST ? name : undefined;
}

/** Renvoie l'URL AVIF correspondante quand elle existe, sinon `undefined`. */
export function avifFor(src?: string | undefined): string | undefined {
  if (!src) return undefined;
  if (src.endsWith(".avif")) return src;
  const key = keyFor(src);
  return key ? `/img/${key}.avif` : undefined;
}

/** Dimensions intrinsèques (pour width/height et aspect-ratio cohérents). */
export function dimsFor(src?: string | undefined): { width: number; height: number } | undefined {
  const key = keyFor(src);
  if (!key) return undefined;
  const meta = IMAGE_MANIFEST[key]!;
  return { width: meta.w, height: meta.h };
}

/**
 * Construit un `srcset` multi-largeurs pour le format demandé.
 * Retourne `undefined` si le visuel n'est pas décliné (asset bundlé, logo…).
 */
export function srcSetFor(
  src?: string | undefined,
  format: "webp" | "avif" = "webp",
): string | undefined {
  const key = keyFor(src);
  if (!key) return undefined;
  const meta = IMAGE_MANIFEST[key]!;
  return meta.widths
    .map((w) => `/img/${key}${w === meta.w ? "" : `-${w}w`}.${format} ${w}w`)
    .join(", ");
}

let avifSupport: Promise<boolean> | null = null;

/** Détection runtime du support AVIF (utile pour les préchargements impératifs). */
export function supportsAvif(): Promise<boolean> {
  if (typeof window === "undefined") return Promise.resolve(false);
  if (!avifSupport) {
    avifSupport = new Promise<boolean>((resolve) => {
      const img = new window.Image();
      img.onload = () => resolve(img.width > 0);
      img.onerror = () => resolve(false);
      img.src =
        "data:image/avif;base64,AAAAIGZ0eXBhdmlmAAAAAGF2aWZtaWYxbWlhZk1BMUIAAADybWV0YQAAAAAAAAAoaGRscgAAAAAAAAAAcGljdAAAAAAAAAAAAAAAAGxpYmF2aWYAAAAADnBpdG0AAAAAAAEAAAAeaWxvYwAAAABEAAABAAEAAAABAAABGgAAAB0AAAAoaWluZgAAAAAAAQAAABppbmZlAgAAAAABAABhdjAxQ29sb3IAAAAAamlwcnAAAABLaXBjbwAAABRpc3BlAAAAAAAAAAEAAAABAAAAEHBpeGkAAAAAAwgICAAAAAxhdjFDgQAMAAAAABNjb2xybmNseAACAAIABoAAAAAXaXBtYQAAAAAAAAABAAEEAQKDBAAAACVtZGF0EgAKCBgABogQEDQgMgkQAAAAB8dSLfI=";
    });
  }
  return avifSupport;
}
